import { useCallback, useEffect, useRef } from 'react';
import { useSignalRListener } from '../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../config/signalr';

interface ReportEventPayload {
  environment?: string;
  workflow_id?: string;
  period_id?: string;
  [key: string]: unknown;
}

interface UseReportSignalRSyncArgs {
  periodId?: string | null;
  workflowId?: string | null;
  /** Called (once per event burst) when a report-related SignalR event
   *  arrives for the current workflow / period. Wire this to your local
   *  `period.refetch()`. */
  onReportEvent: () => void;
}

// 250ms coalescing window — a single backend transition often fans out to
// several events (e.g. AutomationReportTaskUpdate + WorkflowPeriodUpdate).
// Matches the coalescing used by `useWorkflowChromeStatusSync`.
const COALESCE_MS = 250;

/**
 * Refetches the report page's period record in near-real-time by listening
 * to the SignalR events that carry report progress. Complements the
 * chrome-level `useWorkflowChromeStatusSync` (which invalidates
 * GET_PERIOD_BY_ID globally) with an explicit page-level refetch so
 * consumers relying on the `period` reference (e.g. the "Regenerate Report"
 * gate) update the moment the event arrives.
 *
 * Environment filtering is handled inside `useSignalRListener`. This hook
 * additionally filters by workflow / period so events for OTHER periods
 * that happen to share the connection don't cause spurious refetches.
 */
export function useReportSignalRSync({
  periodId,
  workflowId,
  onReportEvent
}: UseReportSignalRSyncArgs): void {
  // Refs so the once-registered listener always sees the latest ids +
  // callback without needing to be re-registered on every render.
  const periodIdRef = useRef(periodId);
  const workflowIdRef = useRef(workflowId);
  const onReportEventRef = useRef(onReportEvent);
  useEffect(() => {
    periodIdRef.current = periodId;
    workflowIdRef.current = workflowId;
    onReportEventRef.current = onReportEvent;
  });

  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (pendingTimerRef.current != null) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, []);

  const matchesCurrentContext = useCallback((payload?: ReportEventPayload): boolean => {
    const currentPeriodId = periodIdRef.current;
    const currentWorkflowId = workflowIdRef.current;
    if (!currentPeriodId) return false;
    if (payload?.workflow_id && payload.workflow_id !== currentWorkflowId) return false;
    if (payload?.period_id && payload.period_id !== currentPeriodId) return false;
    return true;
  }, []);

  const scheduleRefetch = useCallback(() => {
    if (pendingTimerRef.current != null) return;
    pendingTimerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;
      onReportEventRef.current?.();
    }, COALESCE_MS);
  }, []);

  const handleReportEvent = useCallback(
    (payload?: ReportEventPayload) => {
      if (!matchesCurrentContext(payload)) return;
      scheduleRefetch();
    },
    [matchesCurrentContext, scheduleRefetch]
  );

  // Report progress + parent workflow-period touch events. Automation-report
  // is the primary signal ("Regenerate Report" transitions); workflow-period
  // covers any other backend flow that mutates the period record.
  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED, handleReportEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_WORKFLOW_PERIOD_UPDATED, handleReportEvent);
}
